import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import type { Json } from "@/integrations/supabase/types";

export interface Trip {
  id: string;
  user_id: string;
  tripit_id: string | null;
  display_name: string;
  start_date: string | null;
  end_date: string | null;
  destination: string | null;
  trip_data: TripData | null;
  email_source: string | null;
  last_synced_at: string;
  created_at: string;
  // Cover photo fields (Unsplash)
  cover_photo_id: string | null;
  cover_photo_url: string | null;
  cover_photo_blur_hash: string | null;
  cover_photo_alt_description: string | null;
  cover_photo_photographer_name: string | null;
  cover_photo_photographer_username: string | null;
  cover_photo_position_y: number | null;
}

export interface Passenger {
  name: string;
  seat_number?: string;
  ticket_number?: string;
  confirmation_number?: string;  // Per-passenger booking reference
  frequent_flyer_number?: string;
  meal_preference?: string;
}

export interface FlightPricing {
  retail_price: number;
  currency: string;
  flight_numbers: string[];
  confirmation_number?: string;
  fetched_at: string;
}

export interface HotelPricing {
  retail_price: number;
  currency: string;
  hotel_name: string;
  check_in_date: string;
  confirmation_number?: string;
  fetched_at: string;
}

// Unified pricing interface for all item types
export interface ItemPricing {
  // User-entered data
  price_paid?: number;           // Cash portion paid
  price_paid_currency?: string;  // Currency code (e.g., "USD")
  taxes_fees_paid?: number;      // Taxes/fees paid in cash
  points_used?: number;          // Points/miles redeemed
  points_program?: string;       // e.g., "United MileagePlus", "Marriott Bonvoy"
  is_points_redemption?: boolean;

  // From API enrichment
  retail_price?: number;
  retail_currency?: string;
  retail_fetched_at?: string;
}

export interface TripData {
  flights?: FlightSegment[];
  flight_pricings?: FlightPricing[];
  hotels?: HotelReservation[];
  hotel_pricings?: HotelPricing[];
  cars?: CarReservation[];
  activities?: Activity[];
  cruises?: CruiseReservation[];
  notes?: string;
  passenger_name?: string;
  confirmation_number?: string;
}

export interface FlightSegment {
  id?: string;
  flight_number: string;
  airline: string;
  departure_airport: string;
  departure_city?: string;
  departure_time: string;
  departure_date?: string;
  departure_timezone?: string;
  departure_terminal?: string;
  departure_gate?: string;
  arrival_airport: string;
  arrival_city?: string;
  arrival_time: string;
  arrival_date?: string;
  arrival_timezone?: string;
  arrival_terminal?: string;
  arrival_gate?: string;
  class?: string;
  cabin_class?: string;
  status?: string;
  confirmation_number?: string;
  additional_confirmation_numbers?: string[];  // For multiple booking references (e.g., different passengers)
  aircraft?: string;
  stops?: number;
  meals?: string;
  seat?: string;
  delay?: number;
  enriched?: boolean;
  passengers?: Passenger[];
  duration_minutes?: number;
  airline_logo?: string;
  legroom?: string;
  overnight?: boolean;
  often_delayed?: boolean;
  operated_by?: string;
  pricing?: ItemPricing;
  source_email_id?: string;
}

export interface HotelReservation {
  id?: string;
  hotel_name: string;
  address: string;
  city: string;
  check_in_date: string;
  check_in_time?: string;
  check_out_date: string;
  check_out_time?: string;
  confirmation_number?: string;
  room_type?: string;
  guests?: { name: string }[];
  // Enrichment fields from Booking.com
  rating?: number;
  rating_label?: string;
  review_count?: number;
  stars?: number;
  hotel_image?: string;
  booking_url?: string;
  enriched?: boolean;
  pricing?: ItemPricing;
  source_email_id?: string;
}

export interface CarReservation {
  id?: string;
  rental_company: string;
  pickup_location?: string;
  pickup_date?: string;
  pickup_time?: string;
  dropoff_location?: string;
  dropoff_date?: string;
  dropoff_time?: string;
  car_type?: string;
  confirmation_number?: string;
  pricing?: ItemPricing;
  source_email_id?: string;
}

export interface Activity {
  id?: string;
  name: string;
  date: string;
  time?: string;
  duration?: string;
  location?: string;
  operator?: string; // Company/provider (e.g., "Disney Cruise Line", "Viator")
  notes?: string;
  confirmation_number?: string;
  pricing?: ItemPricing;
  source_email_id?: string;
}

export interface CruiseItineraryStop {
  port_name: string;
  country?: string;
  arrival_date?: string;
  arrival_time?: string;
  departure_date?: string;
  departure_time?: string;
  is_embarkation?: boolean;
  is_disembarkation?: boolean;
}

export interface CruiseReservation {
  id?: string;
  cruise_line: string;
  ship_name: string;
  departure_port: string;
  departure_date: string;
  arrival_port?: string;
  arrival_date?: string;
  cabin_number?: string;
  cabin_type?: string;
  deck?: string;
  confirmation_number?: string;
  additional_confirmation_numbers?: string[];  // For multiple booking references
  itinerary_stops?: CruiseItineraryStop[];
  cruise_title?: string;
  enriched?: boolean;
  passengers?: { name: string }[];
  pricing?: ItemPricing;
  source_email_id?: string;
}

export function useTrips() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["trips", user?.id],
    queryFn: async () => {
      if (!user) return [];
      
      const { data, error } = await supabase
        .from("trips")
        .select("*")
        .eq("user_id", user.id)
        .order("start_date", { ascending: false });
      
      if (error) throw error;
      return data as Trip[];
    },
    enabled: !!user,
  });
}

export function useTrip(tripId: string | undefined) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["trip", tripId],
    queryFn: async () => {
      if (!user || !tripId) return null;
      
      const { data, error } = await supabase
        .from("trips")
        .select("*")
        .eq("id", tripId)
        .eq("user_id", user.id)
        .maybeSingle();
      
      if (error) throw error;
      return data as Trip | null;
    },
    enabled: !!user && !!tripId,
  });
}

export function useCreateTrip() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({
      displayName,
      destination,
      startDate,
      endDate
    }: {
      displayName: string;
      destination?: string;
      startDate?: string;
      endDate?: string;
    }) => {
      if (!user) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from("trips")
        .insert({
          user_id: user.id,
          display_name: displayName,
          destination: destination || null,
          start_date: startDate || null,
          end_date: endDate || null,
          tripit_id: null,
          email_source: "manual",
          trip_data: { flights: [], flight_pricings: [], hotels: [], cars: [], activities: [], cruises: [] },
        })
        .select()
        .single();

      if (error) throw error;

      // Auto-assign cover photo if destination is provided
      if (destination && data.id) {
        supabase.functions.invoke('auto-assign-cover-photo', {
          body: { tripId: data.id, destination },
        }).catch((err) => {
          console.warn('Failed to auto-assign cover photo:', err);
        });
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["trips"] });
      toast.success("Trip created successfully");
    },
    onError: (error) => {
      toast.error("Failed to create trip: " + error.message);
    },
  });
}

export function useUpdateTrip() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({
      tripId,
      displayName,
      destination,
      startDate,
      endDate,
      tripData,
      previousDestination,
    }: {
      tripId: string;
      displayName?: string;
      destination?: string;
      startDate?: string;
      endDate?: string;
      tripData?: TripData;
      previousDestination?: string | null;
    }) => {
      if (!user) throw new Error("Not authenticated");

      const updateData: Record<string, unknown> = {};
      if (displayName !== undefined) updateData.display_name = displayName;
      if (destination !== undefined) updateData.destination = destination;
      if (startDate !== undefined) updateData.start_date = startDate || null;
      if (endDate !== undefined) updateData.end_date = endDate || null;
      if (tripData !== undefined) updateData.trip_data = tripData;

      const { data, error } = await supabase
        .from("trips")
        .update(updateData)
        .eq("id", tripId)
        .eq("user_id", user.id)
        .select()
        .single();

      if (error) throw error;

      // Auto-assign cover photo if destination changed
      const destinationChanged = destination !== undefined && destination !== previousDestination;
      if (destinationChanged && destination) {
        supabase.functions.invoke('auto-assign-cover-photo', {
          body: { tripId, destination },
        }).catch((err) => {
          console.warn('Failed to auto-assign cover photo:', err);
        });
      }

      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["trips"] });
      queryClient.invalidateQueries({ queryKey: ["trip", data.id] });
      toast.success("Trip updated successfully");
    },
    onError: (error) => {
      toast.error("Failed to update trip: " + error.message);
    },
  });
}

export function useDeleteTrip() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (tripId: string) => {
      if (!user) throw new Error("Not authenticated");
      
      const { error } = await supabase
        .from("trips")
        .delete()
        .eq("id", tripId)
        .eq("user_id", user.id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["trips"] });
      toast.success("Trip deleted successfully");
    },
    onError: (error) => {
      toast.error("Failed to delete trip: " + error.message);
    },
  });
}

export function useMergeTrips() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ 
      targetTripId, 
      sourceTripIds 
    }: { 
      targetTripId: string; 
      sourceTripIds: string[];
    }) => {
      if (!user) throw new Error("Not authenticated");
      
      // Fetch all trips to merge
      const { data: trips, error: fetchError } = await supabase
        .from("trips")
        .select("*")
        .in("id", [targetTripId, ...sourceTripIds])
        .eq("user_id", user.id);
      
      if (fetchError) throw fetchError;
      if (!trips || trips.length === 0) throw new Error("No trips found");
      
      const targetTrip = trips.find(t => t.id === targetTripId) as Trip | undefined;
      const sourceTrips = trips.filter(t => sourceTripIds.includes(t.id)) as Trip[];
      
      if (!targetTrip) throw new Error("Target trip not found");
      
      const targetData = targetTrip.trip_data as TripData | null;
      
      // Merge trip_data from all source trips into target
      const mergedTripData: TripData = {
        flights: [...(targetData?.flights || [])],
        flight_pricings: [...(targetData?.flight_pricings || [])],
        hotels: [...(targetData?.hotels || [])],
        cars: [...(targetData?.cars || [])],
        activities: [...(targetData?.activities || [])],
        cruises: [...(targetData?.cruises || [])],
      };

      for (const source of sourceTrips) {
        const sourceData = source.trip_data as TripData | null;
        if (sourceData?.flights) {
          mergedTripData.flights!.push(...sourceData.flights);
        }
        if (sourceData?.flight_pricings) {
          mergedTripData.flight_pricings!.push(...sourceData.flight_pricings);
        }
        if (sourceData?.hotels) {
          mergedTripData.hotels!.push(...sourceData.hotels);
        }
        if (sourceData?.cars) {
          mergedTripData.cars!.push(...sourceData.cars);
        }
        if (sourceData?.activities) {
          mergedTripData.activities!.push(...sourceData.activities);
        }
        if (sourceData?.cruises) {
          mergedTripData.cruises!.push(...sourceData.cruises);
        }
      }
      
      // Update target trip with merged data
      const { error: updateError } = await supabase
        .from("trips")
        .update({ trip_data: mergedTripData as unknown as Json })
        .eq("id", targetTripId)
        .eq("user_id", user.id);
      
      if (updateError) throw updateError;
      
      // Delete source trips
      const { error: deleteError } = await supabase
        .from("trips")
        .delete()
        .in("id", sourceTripIds)
        .eq("user_id", user.id);
      
      if (deleteError) throw deleteError;
      
      return targetTripId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["trips"] });
      toast.success("Trips merged successfully");
    },
    onError: (error) => {
      toast.error("Failed to merge trips: " + error.message);
    },
  });
}

export function useCreateShareableLink() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ tripId, expiresAt }: { tripId: string; expiresAt?: string }) => {
      if (!user) throw new Error("Not authenticated");
      
      const { data, error } = await supabase
        .from("shareable_links")
        .insert({
          trip_id: tripId,
          user_id: user.id,
          expires_at: expiresAt || null,
        })
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["shareableLinks"] });
      toast.success("Shareable link created");
    },
    onError: (error) => {
      toast.error("Failed to create link: " + error.message);
    },
  });
}

type ItemType = "flights" | "hotels" | "cars" | "activities" | "cruises";
type TripItem = FlightSegment | HotelReservation | CarReservation | Activity | CruiseReservation;

export function useUpdateTripItem() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ 
      tripId, 
      itemType, 
      index, 
      item 
    }: { 
      tripId: string; 
      itemType: ItemType; 
      index: number; 
      item: TripItem;
    }) => {
      if (!user) throw new Error("Not authenticated");
      
      // Fetch current trip
      const { data: trip, error: fetchError } = await supabase
        .from("trips")
        .select("*")
        .eq("id", tripId)
        .eq("user_id", user.id)
        .single();
      
      if (fetchError) throw fetchError;
      
      const tripData = (trip.trip_data as TripData) || { flights: [], flight_pricings: [], hotels: [], cars: [], activities: [], cruises: [] };
      const items = [...(tripData[itemType] || [])];
      items[index] = item;
      
      const updatedTripData = { ...tripData, [itemType]: items };
      
      const { error: updateError } = await supabase
        .from("trips")
        .update({ trip_data: updatedTripData as unknown as Json })
        .eq("id", tripId)
        .eq("user_id", user.id);
      
      if (updateError) throw updateError;
      return tripId;
    },
    onSuccess: (tripId) => {
      queryClient.invalidateQueries({ queryKey: ["trips"] });
      queryClient.invalidateQueries({ queryKey: ["trip", tripId] });
    },
  });
}

export function useDeleteTripItem() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ 
      tripId, 
      itemType, 
      index 
    }: { 
      tripId: string; 
      itemType: ItemType; 
      index: number;
    }) => {
      if (!user) throw new Error("Not authenticated");
      
      // Fetch current trip
      const { data: trip, error: fetchError } = await supabase
        .from("trips")
        .select("*")
        .eq("id", tripId)
        .eq("user_id", user.id)
        .single();
      
      if (fetchError) throw fetchError;
      
      const tripData = (trip.trip_data as TripData) || { flights: [], flight_pricings: [], hotels: [], cars: [], activities: [], cruises: [] };
      const items = [...(tripData[itemType] || [])];
      items.splice(index, 1);
      
      const updatedTripData = { ...tripData, [itemType]: items };
      
      const { error: updateError } = await supabase
        .from("trips")
        .update({ trip_data: updatedTripData as unknown as Json })
        .eq("id", tripId)
        .eq("user_id", user.id);
      
      if (updateError) throw updateError;
      return tripId;
    },
    onSuccess: (tripId) => {
      queryClient.invalidateQueries({ queryKey: ["trips"] });
      queryClient.invalidateQueries({ queryKey: ["trip", tripId] });
    },
  });
}

export function useAddTripItem() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ 
      tripId, 
      itemType, 
      item 
    }: { 
      tripId: string; 
      itemType: ItemType; 
      item: TripItem;
    }) => {
      if (!user) throw new Error("Not authenticated");
      
      // Fetch current trip
      const { data: trip, error: fetchError } = await supabase
        .from("trips")
        .select("*")
        .eq("id", tripId)
        .eq("user_id", user.id)
        .single();
      
      if (fetchError) throw fetchError;
      
      const tripData = (trip.trip_data as TripData) || { flights: [], flight_pricings: [], hotels: [], cars: [], activities: [], cruises: [] };
      const items = [...(tripData[itemType] || []), item];
      
      const updatedTripData = { ...tripData, [itemType]: items };
      
      const { error: updateError } = await supabase
        .from("trips")
        .update({ trip_data: updatedTripData as unknown as Json })
        .eq("id", tripId)
        .eq("user_id", user.id);
      
      if (updateError) throw updateError;
      return tripId;
    },
    onSuccess: (tripId) => {
      queryClient.invalidateQueries({ queryKey: ["trips"] });
      queryClient.invalidateQueries({ queryKey: ["trip", tripId] });
    },
  });
}